import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { ArrowLeft, AlertCircle, Check, Calendar, FileText } from 'lucide-react';

function ModePill({ mode }) {
  const isAuto = (mode || '').toLowerCase() === 'auto';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${isAuto ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{mode || 'manual'}</span>;
}

export default function BillingLogDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try { setLog(await api.billingLogGet(token, id)); }
    catch (e) { toast.show(e?.message || 'Load failed', 'error'); }
    finally { setLoading(false); }
  }, [token, id, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!log) return <div className="p-8 text-center text-gray-500">Billing log not found</div>;

  const errors = log.errors || [];

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="billing-log-detail-page">
      <button onClick={() => navigate('/school/billing-logs')} data-testid="bl-back" className="flex items-center gap-2 text-gray-500 text-sm font-semibold mb-5 hover:text-gray-700"><ArrowLeft size={16} /> Back to Billing Logs</button>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6" data-testid="bl-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2"><FileText size={22} className="text-indigo-600" /> Billing Run</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Calendar size={14} /> {log.run_date || log.created_at ? new Date(log.run_date || log.created_at).toLocaleString('en-IN') : '—'}</span>
              <ModePill mode={log.mode || log.run_mode} />
              {log.period && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">{log.period}</span>}
            </div>
            {(log.plan_name || log.fee_plan_name) && (
              <p className="text-gray-500 text-sm mt-2">Plan: <button onClick={() => log.fee_plan_id && navigate(`/school/fee-plans/${log.fee_plan_id}`)} className="text-indigo-600 font-semibold hover:underline">{log.plan_name || log.fee_plan_name}</button></p>
            )}
          </div>
        </div>
      </div>

      {/* Result KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="bl-kpis">
        {[
          ['Invoices Created', log.invoices_created, 'text-indigo-600 bg-indigo-50'],
          ['Students Billed', log.students_billed, 'text-emerald-600 bg-emerald-50'],
          ['Skipped', log.skipped_already_billed ?? log.skipped, 'text-amber-600 bg-amber-50'],
          ['Errors', errors.length || log.error_count || 0, 'text-red-600 bg-red-50'],
        ].map(([label, count, cls]) => (
          <div key={label} className={`rounded-2xl p-5 text-center border border-gray-100 shadow-sm ${cls.split(' ').slice(1).join(' ')}`}>
            <p className={`text-3xl font-extrabold tabular-nums ${cls.split(' ')[0]}`}>{count ?? 0}</p>
            <p className="text-xs font-semibold text-gray-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Success indicator */}
      {errors.length === 0 && (log.invoices_created || 0) > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold mb-6" data-testid="bl-success">
          <Check size={16} /> Billing run completed successfully with {log.invoices_created} invoice(s) created
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-6" data-testid="bl-errors">
          <h3 className="text-red-600 text-xs font-bold uppercase mb-3 flex items-center gap-2"><AlertCircle size={14} /> Errors ({errors.length})</h3>
          <div className="space-y-2">
            {errors.map((err, i) => (
              <div key={i} className="flex items-start gap-2 text-sm p-2 bg-red-50 rounded-lg">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  {err.customer_id && <span className="text-gray-500 text-xs">Student: {err.customer_id} · </span>}
                  <span className="text-red-700">{err.message || err.error || JSON.stringify(err)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to fee invoices */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="bl-invoice-link">
        <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Created Invoices</h3>
        <p className="text-gray-500 text-sm">
          Fee invoices are created as Sales with <span className="font-mono text-gray-700">sale_type: fee_invoice</span>.
          {' '}View them in the <button onClick={() => navigate('/sales')} className="text-indigo-600 font-semibold hover:underline">Sales</button> page.
        </p>
      </div>
    </div>
  );
}
